import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react';
import {
  Box,
  FormControlLabel,
  Switch,
  FormGroup,
  IconButton,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import { HelpOutline } from '@mui/icons-material';

import OrganizationsDropdown from '@/components/OrganizationsDropdown/OrganizationsDropdown';
import CpdTextField from '@/components/ui/CpdTextField';

const IDENTIFIER_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

const IDENTIFIER_HINT =
  'Stable slug used by automation to find this role, e.g. a role named "General User" should use "general-user". Lowercase letters, numbers and single hyphens only. Leave blank if the role is not referenced by automation.';

const RoleBasicInfoSection = forwardRef(
  ({ role = null, onFormChange = () => {} }, ref) => {
    const [basicInfo, setBasicInfo] = useState({
      name: '',
      identifier: '',
      description: '',
      organizationId: '',
      isDefault: false,
      hasAllFacilitiesAccess: false,
    });

    const [errors, setErrors] = useState({});

    useEffect(() => {
      if (role) {
        setBasicInfo({
          name: role.name || '',
          identifier: role.identifier || '',
          description: role.description || '',
          organizationId: String(role.organization_id || ''),
          isDefault: role.is_default || false,
          hasAllFacilitiesAccess: role.has_all_facilities_access || false,
        });
      } else {
        setBasicInfo({
          name: '',
          identifier: '',
          description: '',
          organizationId: '',
          isDefault: false,
          hasAllFacilitiesAccess: false,
        });
      }
      setErrors({});
    }, [role]);

    useEffect(() => {
      onFormChange(basicInfo);
    }, [basicInfo, onFormChange]);

    const handleFormChange = (field, value) => {
      setBasicInfo((prev) => ({ ...prev, [field]: value }));
      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: null }));
      }
    };

    const identifierError = () => {
      const identifier = basicInfo.identifier.trim();

      if (!identifier) {
        return null;
      }

      if (!IDENTIFIER_PATTERN.test(identifier.toLowerCase())) {
        return 'Use lowercase letters, numbers and single hyphens, e.g. "pcc-general-user"';
      }

      return null;
    };

    const validateForm = () => {
      const newErrors = {};

      if (!basicInfo.name.trim()) {
        newErrors.name = 'Role name is required';
      }

      if (!basicInfo.organizationId) {
        newErrors.organizationId = 'Organization is required';
      }

      const identifierValidationError = identifierError();
      if (identifierValidationError) {
        newErrors.identifier = identifierValidationError;
      }

      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    };

    const isFormValid = () => {
      return (
        basicInfo.name.trim() && basicInfo.organizationId && !identifierError()
      );
    };

    useImperativeHandle(ref, () => ({
      validateForm,
      isFormValid: isFormValid(),
      formData: basicInfo,
    }));

    const visibleIdentifierError = errors.identifier || identifierError();

    return (
      <Box>
        <CpdTextField
          label='Role Name'
          value={basicInfo.name}
          onChange={(e) => handleFormChange('name', e.target.value)}
          required
          error={!!errors.name}
          helperText={errors.name}
        />

        <CpdTextField
          label='Identifier'
          value={basicInfo.identifier}
          onChange={(e) => handleFormChange('identifier', e.target.value)}
          error={!!visibleIdentifierError}
          helperText={visibleIdentifierError || IDENTIFIER_HINT}
          placeholder='pcc-general-user'
          InputProps={{
            endAdornment: (
              <InputAdornment position='end'>
                <Tooltip title={IDENTIFIER_HINT}>
                  <IconButton size='small' edge='end'>
                    <HelpOutline fontSize='small' color='action' />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ),
          }}
        />

        <CpdTextField
          label='Description'
          value={basicInfo.description}
          onChange={(e) => handleFormChange('description', e.target.value)}
          multiline
          rows={2}
        />

        <OrganizationsDropdown
          value={basicInfo.organizationId}
          onChange={(e) => handleFormChange('organizationId', e.target.value)}
          label='Organization'
          required
          error={!!errors.organizationId}
          helperText={errors.organizationId}
        />
        <FormGroup>
          <FormControlLabel
            control={
              <Switch
                checked={basicInfo.isDefault}
                onChange={(e) =>
                  handleFormChange('isDefault', e.target.checked)
                }
                color='primary'
              />
            }
            label='Set as default role for this organization'
            sx={{ mt: 1 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={basicInfo.hasAllFacilitiesAccess}
                onChange={(e) =>
                  handleFormChange('hasAllFacilitiesAccess', e.target.checked)
                }
                color='primary'
              />
            }
            label='Grant access to all facilities in this organization'
            sx={{ mt: 1 }}
          />
        </FormGroup>
      </Box>
    );
  },
);

RoleBasicInfoSection.displayName = 'RoleBasicInfoSection';

export default RoleBasicInfoSection;
